import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import { Button } from '~/ui/buttons';
import { CloseButton } from '~/ui/buttons/CloseButton';

const TooltipContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 9999;
  /* Let clicks / lower UI (e.g. EditorBanners) work outside the yellow tooltip box */
  pointer-events: none;

  b {
    font-family: ralewayBold;
    font-size: 16px;
  }

  .tooltip-container .tooltip-body-text {
    font-size: 14px;
    white-space: pre-line;
  }

  .tooltip-container {
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 427px;
    pointer-events: auto;
  }

  .closeBtn {
    position: absolute;
    top: 10px;
    left: 100%;
    margin-left: -34px;
  }

  button {
    font-family: ralewayBold;
    font-size: 14px;
  }
`;

const Tooltips = () => {
  const {
    showTooltips,
    tooltip,
    nextTooltip,
    prevTooltip,
    setShowTooltips,
    training
  } = useEditorStore();

  useEffect(() => {
    if (window && window.document) {
      setTimeout(() => {
        const element = window.document.getElementById('tooltip-title');
        if (element) {
          element.focus();
        }
      }, 0);
    }
  }, [tooltip]);

  const tooltipList = useMemo(() => {
    const fullList = [
      {
        title: 'Adding Slides',
        body: 'Click to add new slides or chapters. Choose from various templates with images, videos, or interactive quizzes.\n\n',
        position: {
          left: '255px',
          top: '16px'
        },
        arrow: 'left' as const
      },
      {
        // Step index must stay === COMPLIANCE_CONTENT_TOOLTIP_INDEX (tourConstants.ts)
        title: 'Compliance Content',
        body: 'This label indicates the slide contains compliance-related content that may be required for legal purposes.\n\n',
        position: {
          left: '255px',
          bottom: '100px'
        },
        arrow: 'bottom-left' as const
      },
      {
        title: 'Changing Images',
        body: 'Click this Edit icon to open the Media modal, where you can select and change background images, or videos.\n\n',
        position: {
          right: '5px',
          top: '125px'
        },
        arrow: 'top-right' as const
      },
      {
        title: 'Narrations',
        body: 'Bring your slide to life with voice. Upload audio files, or generate speech from text using our natural-sounding AI voices.\n\n',
        position: {
          right: '295px',
          top: '75px'
        },
        arrow: 'top-right' as const
      },
      {
        title: 'Navigation',
        body: 'Browse your course outline here, organized into chapters and slides. Simply click any slide to view its content. Need to reorganize? Just drag and drop slides to create your perfect learning sequence.\n\n',
        position: {
          left: '255px',
          top: '100px'
        },
        arrow: 'left' as const
      },
      {
        title: 'Publish',
        body: 'Once you are done editing, publish the course here, and assign it to your Learners.\n\n',
        position: {
          right: '50px',
          top: '75px'
        },
        arrow: 'top-right' as const
      }
    ];

    if (training?.customizationOfCanonical) {
      const canonicalPublishTitle =
        'Publish the course to push your changes live.\n\n' +
        'After publishing, any changes that you apply to this customized version will be applied automatically in real-time.\n\n' +
        "NOTE: You'll still need to activate the version you want assigned to learners.\n\n";

      return fullList.map((item, i) =>
        i === fullList.length - 1
          ? { ...item, body: canonicalPublishTitle }
          : item
      );
    }

    return fullList.filter(item => item.title !== 'Compliance Content');
  }, [training?.customizationOfCanonical]);

  if (!showTooltips) return null;

  const lastTooltipIndex = tooltipList.length - 1;

  return (
    <TooltipContainer>
      <Tooltip
        placement={tooltipList[tooltip].arrow}
        top={tooltipList[tooltip].position.top}
        bottom={tooltipList[tooltip].position.bottom}
        left={tooltipList[tooltip].position.left}
        right={tooltipList[tooltip].position.right}
        className="tooltip-container"
      >
        <CloseButton
          type="close"
          onClick={() => setShowTooltips(false)}
          autoFocus={false}
        />
        <b id="tooltip-title" tabIndex={0} role="heading" aria-level={2}>
          {tooltipList[tooltip].title}
        </b>
        <span className="tooltip-body-text">{tooltipList[tooltip].body}</span>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'left' }}>
          {tooltip > 0 && (
            <Button
              onClick={prevTooltip}
              style={{
                backgroundColor: 'var(--color-yellow-lt)',
                color: 'var(--color-accent-dk)',
                border: '2px solid var(--color-accent-dk)'
              }}
            >
              Previous
            </Button>
          )}
          {tooltip < lastTooltipIndex && (
            <Button onClick={nextTooltip} tabIndex={0}>
              Next
            </Button>
          )}
          {tooltip === lastTooltipIndex && (
            <Button onClick={() => setShowTooltips(false)} tabIndex={0}>
              Got it
            </Button>
          )}
        </div>
      </Tooltip>
    </TooltipContainer>
  );
};

export default Tooltips;
